/**
 * 安全存储工具
 * 用于加密存储敏感信息，避免明文暴露个人信息
 */

// 加密密钥 - 建议使用环境变量管理
const ENCRYPTION_KEY = "your-secret-key-2024";

/**
 * 简单的异或加密实现
 */
class SecureStorage {
  constructor(key = ENCRYPTION_KEY) {
    this.key = key;
    console.log("SecureStorage 初始化，密钥长度:", this.key.length);
  }

  /**
   * 加密字符串（公开方法，可用于传输加密）
   */
  encrypt(data) {
    try {
      console.log('开始加密，数据长度:', data.length);
      // 先对UTF-8字符串进行编码，确保btoa能正确处理
      const encodedData = encodeURIComponent(data);
      let result = '';
      for (let i = 0; i < encodedData.length; i++) {
        result += String.fromCharCode(
          encodedData.charCodeAt(i) ^ this.key.charCodeAt(i % this.key.length)
        );
      }
      const encrypted = btoa(result);
      console.log('加密成功，加密后长度:', encrypted.length);
      return encrypted;
    } catch (error) {
      console.error('加密失败:', error);
      console.error('原始数据:', data);
      // 降级处理：直接编码存储
      return btoa(encodeURIComponent(data));
    }
  }

  /**
   * 解密字符串（公开方法，可用于传输解密）
   */
  decrypt(encryptedData) {
    try {
      console.log('开始解密，加密数据长度:', encryptedData.length);
      
      // 首先检查数据是否是有效的Base64格式
      if (!this.isValidBase64(encryptedData)) {
        console.log('数据不是有效的Base64格式，可能是明文数据，直接返回');
        return encryptedData;
      }
      
      const data = atob(encryptedData);
      let result = '';
      for (let i = 0; i < data.length; i++) {
        result += String.fromCharCode(
          data.charCodeAt(i) ^ this.key.charCodeAt(i % this.key.length)
        );
      }
      // 解码UTF-8字符串
      const decodedResult = decodeURIComponent(result);
      console.log('解密成功，解密后长度:', decodedResult.length);
      return decodedResult;
    } catch (error) {
      console.error('解密失败:', error);
      console.error('加密数据:', encryptedData);
      // 如果解密失败，可能是明文数据，直接返回
      return encryptedData;
    }
  }

  /**
   * 检查字符串是否是有效的Base64格式
   */
  isValidBase64(str) {
    try {
      // 检查字符串是否只包含Base64字符
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(str)) {
        return false;
      }
      
      // 尝试解码，如果成功说明是有效的Base64
      atob(str);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 加密存储对象
   */
  setItem(key, data) {
    try {
      console.log("开始存储对象:", key, "数据类型:", typeof data);
      const jsonData = JSON.stringify(data);
      console.log("JSON序列化后长度:", jsonData.length);
      const encryptedData = this.encrypt(jsonData);
      localStorage.setItem(key, encryptedData);
      console.log("对象存储成功:", key);
    } catch (error) {
      console.error("加密存储失败:", error);
      console.error("存储的数据:", data);
      // 降级到明文存储
      localStorage.setItem(key, JSON.stringify(data));
    }
  }

  /**
   * 解密获取对象
   */
  getItem(key) {
    try {
      console.log("开始获取对象:", key);
      const encryptedData = localStorage.getItem(key);
      if (!encryptedData) {
        console.log("未找到数据:", key);
        return null;
      }

      console.log("获取到数据长度:", encryptedData.length);
      
      // 尝试解密
      const decryptedData = this.decrypt(encryptedData);
      console.log("解密后数据长度:", decryptedData.length);
      
      // 尝试解析JSON
      try {
        const result = JSON.parse(decryptedData);
        console.log("对象获取成功:", key, "数据类型:", typeof result);
        return result;
      } catch (jsonError) {
        console.log("JSON解析失败，可能是字符串数据:", jsonError);
        return decryptedData;
      }
    } catch (error) {
      console.error("解密获取失败:", error);
      console.error("键名:", key);
      // 尝试直接获取（兼容旧数据）
      try {
        const data = localStorage.getItem(key);
        console.log("尝试直接获取原始数据:", data);
        if (!data) return null;
        
        // 尝试解析JSON
        try {
          return JSON.parse(data);
        } catch {
          return data;
        }
      } catch (fallbackError) {
        console.error("降级获取也失败:", fallbackError);
        return null;
      }
    }
  }

  /**
   * 加密存储字符串
   */
  setString(key, data) {
    try {
      const encryptedData = this.encrypt(data);
      localStorage.setItem(key, encryptedData);
    } catch (error) {
      console.error("加密存储字符串失败:", error);
      localStorage.setItem(key, data);
    }
  }

  /**
   * 解密获取字符串
   */
  getString(key) {
    try {
      const encryptedData = localStorage.getItem(key);
      if (!encryptedData) return null;

      return this.decrypt(encryptedData);
    } catch (error) {
      console.error("解密获取字符串失败:", error);
      return localStorage.getItem(key);
    }
  }

  /**
   * 删除项目
   */
  removeItem(key) {
    localStorage.removeItem(key);
  }

  /**
   * 清空所有数据
   */
  clear() {
    localStorage.clear();
  }
}

// 创建全局实例
const secureStorage = new SecureStorage();

// 测试函数 - 用于验证加密功能
export const testEncryption = () => {
  console.log('=== 开始测试加密功能 ===');
  
  // 测试简单字符串
  const testString = 'Hello World';
  console.log('测试字符串:', testString);
  secureStorage.setString('test_string', testString);
  const retrievedString = secureStorage.getString('test_string');
  console.log('获取的字符串:', retrievedString);
  console.log('字符串测试结果:', testString === retrievedString);
  
  // 测试包含中文字符的字符串
  const chineseString = '你好世界 Hello World';
  console.log('测试中文字符串:', chineseString);
  secureStorage.setString('test_chinese_string', chineseString);
  const retrievedChineseString = secureStorage.getString('test_chinese_string');
  console.log('获取的中文字符串:', retrievedChineseString);
  console.log('中文字符串测试结果:', chineseString === retrievedChineseString);
  
  // 测试简单对象
  const testObject = { name: 'test', value: 123 };
  console.log('测试对象:', testObject);
  secureStorage.setItem('test_object', testObject);
  const retrievedObject = secureStorage.getItem('test_object');
  console.log('获取的对象:', retrievedObject);
  console.log('对象测试结果:', JSON.stringify(testObject) === JSON.stringify(retrievedObject));
  
  // 测试包含中文字符的复杂对象（类似您的数据结构）
  const complexObject = {
    loginStepOneTenants: [
      {
        uniTenantId: "1871857235965046785",
        tenantName: "测试企业",
        staffName: "测试用户",
        roleCode: "admin",
        roleCodeText: "高级管理员",
        statusText: "正常"
      }
    ],
    multipleTenant: true,
    mobile: "15900000000",
    loginType: "password"
  };
  console.log('测试复杂对象:', complexObject);
  secureStorage.setItem('test_complex', complexObject);
  const retrievedComplex = secureStorage.getItem('test_complex');
  console.log('获取的复杂对象:', retrievedComplex);
  console.log('复杂对象测试结果:', JSON.stringify(complexObject) === JSON.stringify(retrievedComplex));
  
  console.log('=== 加密功能测试完成 ===');
};

export default secureStorage;

// 导出便捷方法
export const secureSetItem = (key, data) =>
  secureStorage.setItem(key, data);
export const secureGetItem = (key) => secureStorage.getItem(key);
export const secureSetString = (key, data) =>
  secureStorage.setString(key, data);
export const secureGetString = (key) => secureStorage.getString(key);
export const secureRemoveItem = (key) => secureStorage.removeItem(key);
export const secureClear = () => secureStorage.clear();

// 导出加密/解密方法（用于传输加密）
export const encryptString = (data) => secureStorage.encrypt(data);
export const decryptString = (encryptedData) => secureStorage.decrypt(encryptedData);